import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useContent } from '../content/ContentContext';
import { SignInPanel } from './SignInPanel';
import { HistoryPanel } from './HistoryPanel';

// Discreet edit affordance. Hidden from ordinary visitors: it only appears
// when the URL carries ?edit (Berti's bookmark) or a session already exists.
function editRequested(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('edit');
}

export function EditBar() {
  const { session, editing, setEditing, signOut, isLocal } = useAuth();
  const { storeKind, dirty, pendingCount, save, revert } = useContent();
  const [showSignIn, setShowSignIn] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');

  const visible = editRequested() || !!session;
  if (!visible) return null;

  async function onSave() {
    if (!dirty) return;
    const n = pendingCount;
    const where = storeKind === 'local' ? 'this browser' : 'the live site';
    if (!window.confirm(`Save ${n} change${n === 1 ? '' : 's'} to ${where}?`)) return;
    setSaving(true); setFlash('');
    try {
      await save(session ? { editor: session.editor, token: session.token } : undefined);
      setFlash('Saved ✓');
    } catch {
      setFlash('Some changes failed to save');
    } finally {
      setSaving(false);
      setTimeout(() => setFlash(''), 2600);
    }
  }

  function onRevert() {
    if (!dirty) return;
    const n = pendingCount;
    if (!window.confirm(`Discard ${n} unsaved change${n === 1 ? '' : 's'}?`)) return;
    revert();
    setFlash('Reverted');
    setTimeout(() => setFlash(''), 2000);
  }

  return (
    <>
      <div className={`editbar ${editing ? 'editbar--on' : ''}`}>
        {!session ? (
          <button className="editbar__btn editbar__btn--primary" onClick={() => setShowSignIn(true)}>
            ✎ Sign in to edit
          </button>
        ) : (
          <>
            <button
              className={`editbar__toggle ${editing ? 'is-on' : ''}`}
              onClick={() => setEditing(!editing)}
              aria-pressed={editing}
            >
              <span className="editbar__dot" />
              {editing ? 'Editing' : 'Edit mode'}
            </button>

            {editing && (
              <>
                <button
                  className="editbar__btn editbar__btn--save"
                  onClick={onSave}
                  disabled={!dirty || saving}
                  title={dirty ? `${pendingCount} unsaved` : 'Nothing to save'}
                >
                  {saving ? 'Saving…' : dirty ? `Save (${pendingCount})` : 'Saved'}
                </button>
                <button
                  className="editbar__btn editbar__btn--revert"
                  onClick={onRevert}
                  disabled={!dirty || saving}
                >
                  Revert
                </button>
                <button className="editbar__btn" onClick={() => setShowHistory(true)}>
                  History
                </button>
              </>
            )}

            {flash && <span className="editbar__flash">{flash}</span>}
            <span className="editbar__who" title={session.editor}>{session.editor}</span>
            <button className="editbar__btn" onClick={signOut}>Sign out</button>
          </>
        )}
      </div>

      {editing && isLocal && (
        <div className="editbar__banner">
          Local preview — changes save to this browser only, not the live site.
        </div>
      )}
      {editing && dirty && (
        <div className="editbar__banner editbar__banner--dirty">
          {pendingCount} unsaved change{pendingCount === 1 ? '' : 's'} — press Save to publish, or Revert to discard.
        </div>
      )}

      {showSignIn && <SignInPanel onClose={() => setShowSignIn(false)} />}
      {showHistory && <HistoryPanel onClose={() => setShowHistory(false)} />}
    </>
  );
}
